import { Send, Trash2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { api } from "@/api";
import { formatError, useAuth } from "@/auth";
import { Avatar, MemberChecks, RichText, timeAgo } from "@/components/projects/common";

function MentionInput({ value, onChange, members }) {
  const ref = useRef(null);
  const match = /@([\p{L}\w-]*)$/u.exec(value.slice(0, ref.current?.selectionStart ?? value.length));
  const options = match ? members.filter((m) => m.name.toLowerCase().startsWith(match[1].toLowerCase())).slice(0, 6) : [];
  const pick = (m) => {
    const pos = ref.current.selectionStart;
    const before = value.slice(0, pos).replace(/@([\p{L}\w-]*)$/u, `@${m.name} `);
    onChange(before + value.slice(pos));
    setTimeout(() => ref.current?.focus(), 0);
  };
  return (
    <div className="pj-mention-wrap">
      <textarea ref={ref} rows={3} value={value} onChange={(e) => onChange(e.target.value)} placeholder="Écrire un commentaire… tapez @ pour mentionner quelqu'un" data-testid="comment-input" />
      {options.length > 0 && <ul className="pj-mention-list" data-testid="mention-options">{options.map((m) => <li key={m.id}><button type="button" onMouseDown={(e) => { e.preventDefault(); pick(m); }} data-testid={`mention-option-${m.id}`}><span className="pj-mention-name">{m.name}</span><small>{m.email}</small></button></li>)}</ul>}
    </div>
  );
}

export function CommentThread({ projectId, targetType, targetId, members }) {
  const { user } = useAuth();
  const others = members.filter((m) => m.id !== user.id);
  const [items, setItems] = useState([]);
  const [body, setBody] = useState("");
  const [notify, setNotify] = useState(others.map((m) => m.id));
  const [busy, setBusy] = useState(false);
  const base = `/projects/${projectId}/comments`;
  const load = useCallback(() => api.get(`${base}/${targetType}/${targetId}`).then(({ data }) => setItems(data)).catch(() => {}), [base, targetType, targetId]);
  useEffect(() => { load(); }, [load]);
  const send = async () => {
    if (!body.trim()) return;
    setBusy(true);
    try { await api.post(`${base}/${targetType}/${targetId}`, { body, notify }); setBody(""); load(); } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    setBusy(false);
  };
  const remove = async (c) => { await api.delete(`${base}/${c.id}`).catch(() => {}); load(); };
  return (
    <div className="pj-comments" data-testid="comment-thread">
      <h4>Commentaires ({items.length})</h4>
      {items.map((c) => (
        <div key={c.id} className="pj-comment" data-testid={`comment-${c.id}`}>
          <Avatar name={c.author_name} size={30} />
          <div><header><strong>{c.author_name}</strong><small>{timeAgo(c.created_at)}</small>{(c.author_id === user.id || user.role === "admin") && <button type="button" className="pj-icon-btn" onClick={() => remove(c)} aria-label="Supprimer" data-testid={`comment-delete-${c.id}`}><Trash2 size={13} /></button>}</header><RichText text={c.body} members={members} /></div>
        </div>
      ))}
      <MentionInput value={body} onChange={setBody} members={others} />
      <MemberChecks members={others} value={notify} onChange={setNotify} testid="comment-notify" />
      <button type="button" className="primary-button" onClick={send} disabled={busy || !body.trim()} data-testid="comment-submit"><Send size={15} />Publier</button>
    </div>
  );
}
